import { apiGet, APIURL, cancelSelectEvent, changeSymbolForWidget, getCSSStyles, getHeaderPinButtonHTML, getHeaderPinStateClass, getNoDataHTML, getWidgetDataURI, isHeaderPinned, normalizeWidgetTheme, setupHeaderPin, toggleWidgetTheme } from '../../helpers';
import { WidgetConfig, WidgetEvent, WidgetEventHandler, WidgetEventType } from '../../types';
import companyStatsStyleText from './../../assets/css/showcase/companyStats.css?raw';

type CompanyStatsCard = {
  label: string;
  value: string;
  change: string;
  positive: boolean;
};

export class TraderverseStockCompanyStats {
  private container: HTMLElement;
  private config: WidgetConfig;
  private eventHandlers: Map<WidgetEventType, WidgetEventHandler[]> = new Map();
  private updateInterval: number | null = null;
  private shadowRoot: ShadowRoot;
  private currentData: any = null;
  private widgetResizeObserver: ResizeObserver | null = null;

  constructor(config: WidgetConfig) {
    this.config = { ...this.getDefaultConfig(), ...config };
    this.config.theme = normalizeWidgetTheme(this.config.theme);
    this.container = this.resolveContainer(config.container);

    if ((this.container as any).shadowRoot) {
      this.shadowRoot = (this.container as any).shadowRoot;
    } else {
      this.shadowRoot = this.container.attachShadow({ mode: 'closed' });
    }

    this.init();

    this.widgetResizeObserver = new ResizeObserver(() => {
      this.updateDisplay();
    });
    setTimeout(() => {
      const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
      if (widget) this.widgetResizeObserver?.observe(widget);
    }, 0);
  }

  private fetchSymbols(): Array<{ value: string; label: string }> {
    return [
      { label: 'AAPL', value: 'AAPL' }, { label: 'MSFT', value: 'MSFT' },
      { label: 'GOOGL', value: 'GOOGL' }, { label: 'AMZN', value: 'AMZN' },
      { label: 'NVDA', value: 'NVDA' }, { label: 'META', value: 'META' },
      { label: 'TSLA', value: 'TSLA' }, { label: 'AVGO', value: 'AVGO' },
      { label: 'PEP', value: 'PEP' }, { label: 'COST', value: 'COST' }
    ];
  }

  private getDefaultConfig(): Partial<WidgetConfig> {
    const symbols = this.fetchSymbols();
    return {
      headerPinned: true,
      symbol: symbols[0].value,
      theme: 'default-dark',
      width: '100%',
      height: '420px',
      autosize: true
    };
  }

  private async init(): Promise<void> {
    if (!this.config.symbol) this.config.symbol = this.fetchSymbols()[0].value;
    this.createWidget();
    const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    if (widget) widget.classList.add('loading');
    await this.attachStyles();
    this.setupEventListeners();
    await this.setupChart(this.config.symbol!);
    if (widget) widget.classList.remove('loading');
    this.emit('ready', {});
    this.config.onReady?.();
    cancelSelectEvent(this.shadowRoot);
  }

  private async attachStyles(): Promise<void> {
    try {
      const styleEl = await getCSSStyles();
      this.shadowRoot.appendChild(styleEl);
    } catch (err) {
      console.error('Failed to load CSS file:', err);
    }

    const localStyle = document.createElement('style');
    localStyle.textContent = companyStatsStyleText;
    this.shadowRoot.appendChild(localStyle);
  }

  private resolveContainer(container: string | HTMLElement): HTMLElement {
    if (typeof container === 'string') {
      const el = document.getElementById(container) || document.querySelector(container);
      if (!el) throw new Error(`Container not found: ${container}`);
      return el as HTMLElement;
    }
    return container;
  }

  private createWidget(): void {
    let themeClass = 'defaultDarkMode';
    let dataTheme = 'default-dark';
    const symbolOptions = this.fetchSymbols()
      .map(s => `<option value="${s.value}" ${s.value === this.config.symbol ? 'selected' : ''}>${s.label}</option>`)
      .join('');

    if (this.config.theme === 'white') {
      themeClass = 'whiteMode';
      dataTheme = 'white';
    } else if (this.config.theme === 'black') {
      themeClass = 'blackMode';
      dataTheme = 'black';
    } else if (this.config.theme === 'default-lite') {
  themeClass = 'defaultLightMode';
  dataTheme = 'default-lite';
}

    const widgetHtml = `
      <div style="width: ${typeof this.config.width == "number" ? this.config.width + 'px' : this.config.width};height: ${typeof this.config.height == "number" ? this.config.height + 'px' : this.config.height};" class="widgetBox ${themeClass} loading ${getHeaderPinStateClass(this.config)}" data-theme="${dataTheme}">
        <div class="headTop">
          <h4 class="title stock">${this.getTitleIconSvg()} ${this.config.title || 'Company Stats'}</h4>
          <div class="actionCol">${getHeaderPinButtonHTML(this.config)}<i class="pi pi-ellipsis-v"></i></div>
        </div>
        <div class="inBody">
          <div class="optionsRow">
            <div class="item">
              <select class="symbol-dropdown">
                ${symbolOptions}
              </select>
            </div>
          </div>
          <div class="chartDv" id="companyStatsContainer">
            <div class="company-stats-empty">Loading company stats...</div>
          </div>
        </div>
      </div>
    `;

    this.shadowRoot.innerHTML = widgetHtml;
  }

  private setupEventListeners(): void {
    setupHeaderPin(this.shadowRoot, this.config);
    const dropdown = this.shadowRoot.querySelector('.symbol-dropdown') as HTMLSelectElement;
    dropdown?.addEventListener('change', e => {
      const value = (e.target as HTMLSelectElement).value;
      this.changeSymbol(value);
    });
    if (dropdown && this.config.symbol) dropdown.value = this.config.symbol;
  }

  private async setupChart(symbol: string): Promise<void> {
    try {
      const response = await apiGet({
        method: "POST",
        url: `${APIURL}/v1/company/general`,
        body: { symbol },
      });

      if (!response.ok) throw new Error("Failed to fetch data");
      this.currentData = await response.json();
      this.updateDisplay();
      this.emit('dataUpdate', this.currentData);
    } catch (err) {
      console.error("Error fetching company stats:", err);
      this.renderEmptyState('Unable to load company stats.');
    }
  }

  private updateDisplay(): void {
    if (!this.currentData) {
      setTimeout(() => this.updateDisplay(), 100);
      return;
    }
    this.renderCompanyStats(this.currentData);
  }

  private renderEmptyState(message: string): void {
    const container = this.shadowRoot.querySelector('#companyStatsContainer');
    if (!container) return;
    container.innerHTML = getNoDataHTML(message.replace(/\.$/, ""));
  }

  private renderCompanyStats(data: any): void {
    const container = this.shadowRoot.querySelector('#companyStatsContainer');
    if (!container) return;

    const info = Array.isArray(data?.data) ? data.data[0] : data?.data ?? data;
    if (!info || typeof info !== 'object') {
      this.renderEmptyState('No data available.');
      return;
    }

    const cards: CompanyStatsCard[] = [
      {
        label: 'Investors',
        value: this.formatStatValue(this.pickValue(info, ['investors', 'Investors', 'investor_count']), '0'),
        change: this.formatPercentChange(this.pickValue(info, ['investorGrowth', 'investor_growth', 'investor_change_percent']), '0.00%'),
        positive: this.isPositiveChange(this.pickValue(info, ['investorGrowth', 'investor_growth', 'investor_change_percent']))
      },
      {
        label: 'Traders',
        value: this.formatStatValue(this.pickValue(info, ['traders', 'Traders', 'trader_count']), '0'),
        change: this.formatPercentChange(this.pickValue(info, ['traderGrowth', 'trader_growth', 'trader_change_percent']), '0.00%'),
        positive: this.isPositiveChange(this.pickValue(info, ['traderGrowth', 'trader_growth', 'trader_change_percent']))
      },
      {
        label: 'Followers',
        value: this.formatStatValue(this.pickValue(info, ['followers', 'Followers', 'follower_count']), '0'),
        change: this.formatPercentChange(this.pickValue(info, ['followerGrowth', 'follower_growth', 'follower_change_percent']), '0.00%'),
        positive: this.isPositiveChange(this.pickValue(info, ['followerGrowth', 'follower_growth', 'follower_change_percent']))
      },
      {
        label: 'Conversation',
        value: this.formatStatValue(this.pickValue(info, ['conversation', 'Conversation', 'conversation_count', 'mentions']), '0'),
        change: this.formatPercentChange(this.pickValue(info, ['conversationGrowth', 'conversation_growth', 'conversation_change_percent']), '0.00%'),
        positive: this.isPositiveChange(this.pickValue(info, ['conversationGrowth', 'conversation_growth', 'conversation_change_percent']))
      },
      {
        label: 'Predictions',
        value: this.formatStatValue(this.pickValue(info, ['predictions', 'Predictions', 'prediction_count']), '0'),
        change: this.formatPercentChange(this.pickValue(info, ['predictionGrowth', 'prediction_growth', 'prediction_change_percent']), '0.00%'),
        positive: this.isPositiveChange(this.pickValue(info, ['predictionGrowth', 'prediction_growth', 'prediction_change_percent']))
      }
    ];

    const websiteUrl = this.normalizeWebsiteUrl(this.pickValue(info, ['WebURL', 'web_url', 'website', 'Website']));
    const websiteLabel = websiteUrl ? websiteUrl.replace(/^https?:\/\//, '') : 'N/A';

    container.innerHTML = `
      <div class="companyStatsMain">
        <div class="companyStatsGrid">
          ${cards.map((card, index) => `
            <div class="companyStatCard ${index === 4 ? 'single' : ''}">
              <div class="decor" aria-hidden="true">
                ${this.getDecorSvg(index)}
              </div>
              <div class="content">
                <h5>${card.label}</h5>
                <div class="metricLine">
                  <span class="value">${card.value}</span>
                  <span class="change ${card.positive ? 'positive' : 'negative'}">
                    ${this.getTrendArrowSvg(card.positive)}
                    ${card.change}
                  </span>
                </div>
                <p>This Month</p>
              </div>
            </div>
          `).join('')}
        </div>

        <div class="companyWebsiteCard">
          <div class="websiteInfo">
            <span class="websiteIcon">${this.getWebsiteIconSvg()}</span>
            <div>
              <h5>Website</h5>
              ${websiteUrl ? `<a href="${websiteUrl}" target="_blank" rel="noopener noreferrer">${websiteLabel}</a>` : `<p>N/A</p>`}
            </div>
          </div>
          ${websiteUrl ? `<a href="${websiteUrl}" target="_blank" rel="noopener noreferrer" class="websiteLink" aria-label="Open website">${this.getExternalLinkSvg()}</a>` : `<span class="websiteLink disabled">${this.getExternalLinkSvg()}</span>`}
        </div>
      </div>
    `;
  }

  private pickValue(raw: Record<string, any>, keys: string[]): any {
    for (const key of keys) {
      if (raw[key] !== undefined && raw[key] !== null && raw[key] !== '') {
        return raw[key];
      }
    }
    return null;
  }

  private formatStatValue(value: any, fallback: string): string {
    if (value == null || value === '') return fallback;
    if (typeof value === 'number') return Number.isInteger(value) ? `${value}` : value.toFixed(0);
    return String(value);
  }

  private formatPercentChange(value: any, fallback: string): string {
    if (value == null || value === '') return fallback;
    if (typeof value === 'number') return `${value.toFixed(2)}%`;
    const text = String(value).trim();
    if (!text) return fallback;
    return text.includes('%') ? text : `${text}%`;
  }

  private isPositiveChange(value: any): boolean {
    if (value == null || value === '') return true;
    if (typeof value === 'number') return value >= 0;
    return !String(value).trim().startsWith('-');
  }

  private normalizeWebsiteUrl(value: any): string | null {
    if (!value) return null;
    const url = String(value).trim();
    if (!url) return null;
    return /^https?:\/\//i.test(url) ? url : `https://${url}`;
  }

  private getDecorSvg(index: number): string {
    const clipId = `decorClip-${index}`;
    return `<svg width="68" height="86" viewBox="0 0 68 86" fill="none" xmlns="http://www.w3.org/2000/svg"><g clip-path="url(#${clipId})"><ellipse cx="57.5" cy="89" rx="53.5" ry="52" fill="currentColor" fill-opacity="0.22"/><ellipse cx="87.5" cy="34" rx="43.5" ry="37" fill="currentColor" fill-opacity="0.12"/></g><defs><clipPath id="${clipId}"><rect width="67.9005" height="85.0827" rx="10" fill="white"/></clipPath></defs></svg>`;
  }

  private getWebsiteIconSvg(): string {
    return `<svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg"><rect width="29.87" height="29.875" rx="14.935" fill="#3FA2FF"/><path d="M17.5792 5.3644C17.0542 5.20959 16.5147 5.10906 15.9692 5.0644C14.7624 4.92164 13.5393 5.01677 12.3692 5.3444L12.2992 5.3644C10.201 5.94055 8.35014 7.19004 7.03136 8.9207C5.71257 10.6514 4.99887 12.7674 5 14.9432C5.00114 17.1191 5.71706 19.2343 7.03765 20.9636C8.35824 22.6929 10.2104 23.9404 12.3092 24.5144C13.0773 24.7319 13.8709 24.8462 14.6692 24.8544C14.7582 24.8704 14.8487 24.8771 14.9392 24.8744C15.8282 24.8755 16.7131 24.7544 17.5692 24.5144C19.6662 23.9385 21.5161 22.6901 22.835 20.9611C24.1539 19.232 24.8689 17.1179 24.87 14.9432C24.8711 12.7686 24.1584 10.6536 22.8413 8.92323C21.5242 7.1928 19.6756 5.94253 17.5792 5.3644ZM17.3092 6.3244C18.604 6.68261 19.8014 7.32804 20.8125 8.21272C21.8236 9.0974 22.6222 10.1986 23.1492 11.4344H18.8892C18.38 9.47141 17.4445 7.64475 16.1492 6.0844C16.5412 6.13562 16.929 6.21584 17.3092 6.3244ZM14.9392 6.2344C16.3047 7.71467 17.3018 9.49628 17.8492 11.4344H12.0092C12.5537 9.49138 13.5588 7.70831 14.9392 6.2364V6.2344ZM18.0992 12.4344C18.419 14.0891 18.419 15.7897 18.0992 17.4444H11.7792C11.61 16.6219 11.5262 15.7841 11.5292 14.9444C11.527 14.1014 11.6107 13.2604 11.7792 12.4344H18.0992ZM12.4992 6.3444L12.5692 6.3244C12.9504 6.22239 13.3379 6.14555 13.7292 6.0944C12.4257 7.64778 11.4861 9.47299 10.9792 11.4364H6.73416C7.25915 10.2144 8.04845 9.12396 9.04528 8.2435C10.0421 7.36304 11.2217 6.71446 12.4992 6.3444ZM5.99916 14.9444C5.99918 14.0941 6.12384 13.2485 6.36916 12.4344H10.7592C10.6072 13.2625 10.5302 14.1025 10.5292 14.9444C10.5308 15.7829 10.6078 16.6196 10.7592 17.4444H6.36916C6.12339 16.6338 5.99871 15.7914 5.99916 14.9444ZM12.5692 23.5544C11.2731 23.1989 10.0743 22.5544 9.06291 21.6694C8.05147 20.7844 7.2536 19.6818 6.72916 18.4444H10.9692C11.4809 20.4124 12.4275 22.2406 13.7392 23.7944C13.3437 23.7437 12.9526 23.6634 12.5692 23.5544ZM12.0092 18.4444H17.8492C17.3089 20.3884 16.3111 22.1749 14.9392 23.6544C13.5681 22.1711 12.5645 20.3865 12.0092 18.4444ZM17.3092 23.5544C16.9234 23.6538 16.5329 23.7339 16.1392 23.7944C17.4381 22.235 18.3771 20.4083 18.8892 18.4444H23.1492C22.6235 19.681 21.8252 20.7829 20.814 21.6677C19.8027 22.5526 18.6046 23.1975 17.3092 23.5544ZM19.1092 17.4444C19.4194 15.7888 19.4194 14.09 19.1092 12.4344H23.4992C23.7489 13.2475 23.8736 14.0938 23.8692 14.9444C23.8714 15.7908 23.7501 16.633 23.5092 17.4444H19.1092Z" fill="white"/></svg>`;
  }

  private getExternalLinkSvg(): string {
    return `<svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3 1.5C2.60218 1.5 2.22064 1.65804 1.93934 1.93934C1.65804 2.22064 1.5 2.60218 1.5 3V9C1.5 9.39782 1.65804 9.77936 1.93934 10.0607C2.22064 10.342 2.60218 10.5 3 10.5H9C9.39782 10.5 9.77936 10.342 10.0607 10.0607C10.342 9.77936 10.5 9.39782 10.5 9V7.5C10.5 7.30109 10.579 7.11032 10.7197 6.96967C10.8603 6.82902 11.0511 6.75 11.25 6.75C11.4489 6.75 11.6397 6.82902 11.7803 6.96967C11.921 7.11032 12 7.30109 12 7.5V9C12 9.79565 11.6839 10.5587 11.1213 11.1213C10.5587 11.6839 9.79565 12 9 12H3C2.20435 12 1.44129 11.6839 0.87868 11.1213C0.31607 10.5587 0 9.79565 0 9V3C0 2.20435 0.31607 1.44129 0.87868 0.87868C1.44129 0.31607 2.20435 0 3 0H4.5C4.69891 0 4.88968 0.0790178 5.03033 0.21967C5.17098 0.360322 5.25 0.551088 5.25 0.75C5.25 0.948912 5.17098 1.13968 5.03033 1.28033C4.88968 1.42098 4.69891 1.5 4.5 1.5H3ZM7.5 1.5C7.30109 1.5 7.11032 1.42098 6.96967 1.28033C6.82902 1.13968 6.75 0.948912 6.75 0.75C6.75 0.551088 6.82902 0.360322 6.96967 0.21967C7.11032 0.0790178 7.30109 0 7.5 0H11.25C11.4489 0 11.6397 0.0790178 11.7803 0.21967C11.921 0.360322 12 0.551088 12 0.75V4.5C12 4.69891 11.921 4.88968 11.7803 5.03033C11.6397 5.17098 11.4489 5.25 11.25 5.25C11.0511 5.25 10.8603 5.17098 10.7197 5.03033C10.579 4.88968 10.5 4.69891 10.5 4.5V2.5605L8.031 5.031C7.96127 5.10073 7.87848 5.15605 7.78738 5.19379C7.69627 5.23152 7.59862 5.25095 7.5 5.25095C7.40138 5.25095 7.30373 5.23152 7.21262 5.19379C7.12152 5.15605 7.03873 5.10073 6.969 5.031C6.89927 4.96127 6.84395 4.87848 6.80621 4.78738C6.76848 4.69627 6.74905 4.59862 6.74905 4.5C6.74905 4.40138 6.76848 4.30373 6.80621 4.21262C6.84395 4.12152 6.89927 4.03873 6.969 3.969L9.4395 1.5H7.5Z" fill="currentColor"/></svg>`;
  }

  private getTrendArrowSvg(isPositive: boolean): string {
    return isPositive
      ? `<svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M5 1.25L8.125 6.875H1.875L5 1.25Z" fill="currentColor"/></svg>`
      : `<svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M5 8.75L1.875 3.125H8.125L5 8.75Z" fill="currentColor"/></svg>`;
  }

  private changeSymbol(stockId: string, stock: any = null): void {
    changeSymbolForWidget(this.shadowRoot, this.config, stock, stockId, (stockId: any) => this.setupChart(stockId), (type: any, data: any) => this.emit(type, data));
  }

  public on(type: WidgetEventType, handler: WidgetEventHandler): void {
    if (!this.eventHandlers.has(type)) this.eventHandlers.set(type, []);
    this.eventHandlers.get(type)!.push(handler);
  }

  public off(type: WidgetEventType, handler: WidgetEventHandler): void {
    const list = this.eventHandlers.get(type);
    if (list) this.eventHandlers.set(type, list.filter(h => h !== handler));
  }

  public setSymbol(symbol: string): void {
    this.changeSymbol(symbol);
    const dropdown = this.shadowRoot.querySelector('.symbol-dropdown') as HTMLSelectElement;
    if (dropdown) dropdown.value = symbol;
  }

  public async getWidgetImage(): Promise<string> {
    const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    return await getWidgetDataURI(widget);
  }

  public setTheme(theme: 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite'): void {
    if (!this.shadowRoot) return;

    this.config.theme = toggleWidgetTheme(
      this.shadowRoot,
      theme
    ) as 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite';

    this.updateDisplay();
  }

  public destroy(): void {
    if (this.updateInterval) { clearInterval(this.updateInterval); this.updateInterval = null; }
    if (this.widgetResizeObserver) { this.widgetResizeObserver.disconnect(); this.widgetResizeObserver = null; }
    this.eventHandlers.clear(); this.shadowRoot.innerHTML = '';
  }

  private emit(type: WidgetEventType, data: any): void {
    const handlers = this.eventHandlers.get(type);
    if (handlers) { const evt: WidgetEvent = { type, data }; handlers.forEach(h => h(evt)); }
  }
  private getTitleIconSvg(): string {
    return `<svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M8.59998 23.625C8.08748 23.625 7.66248 23.2 7.66248 22.6875V20.1C7.66248 19.5875 8.08748 19.1625 8.59998 19.1625C9.11248 19.1625 9.53748 19.5875 9.53748 20.1V22.6875C9.53748 23.2125 9.11248 23.625 8.59998 23.625Z" fill="currentColor"/> <path d="M15 23.625C14.4875 23.625 14.0625 23.2 14.0625 22.6875V17.5C14.0625 16.9875 14.4875 16.5625 15 16.5625C15.5125 16.5625 15.9375 16.9875 15.9375 17.5V22.6875C15.9375 23.2125 15.5125 23.625 15 23.625Z" fill="currentColor"/> <path d="M21.4 23.625C20.8875 23.625 20.4625 23.2 20.4625 22.6875V14.9125C20.4625 14.4 20.8875 13.975 21.4 13.975C21.9125 13.975 22.3375 14.4 22.3375 14.9125V22.6875C22.3375 23.2125 21.925 23.625 21.4 23.625Z" fill="currentColor"/> <path d="M8.59997 16.475C8.17497 16.475 7.79997 16.1875 7.68747 15.7625C7.56247 15.2625 7.86247 14.75 8.37497 14.625C12.975 13.475 17.025 10.9625 20.1125 7.37502L20.6875 6.70002C21.025 6.31252 21.6125 6.26252 22.0125 6.60002C22.4 6.93752 22.45 7.52502 22.1125 7.92502L21.5375 8.60002C18.2 12.5 13.8 15.2125 8.82497 16.45C8.74997 16.475 8.67497 16.475 8.59997 16.475Z" fill="currentColor"/> <path d="M21.4001 11.9C20.8876 11.9 20.4626 11.475 20.4626 10.9625V8.25H17.7375C17.225 8.25 16.8 7.825 16.8 7.3125C16.8 6.8 17.225 6.375 17.7375 6.375H21.4001C21.9126 6.375 22.3376 6.8 22.3376 7.3125V10.975C22.3376 11.4875 21.9251 11.9 21.4001 11.9Z" fill="currentColor"/> <path d="M18.75 28.4375H11.25C4.4625 28.4375 1.5625 25.5375 1.5625 18.75V11.25C1.5625 4.4625 4.4625 1.5625 11.25 1.5625H18.75C25.5375 1.5625 28.4375 4.4625 28.4375 11.25V18.75C28.4375 25.5375 25.5375 28.4375 18.75 28.4375ZM11.25 3.4375C5.4875 3.4375 3.4375 5.4875 3.4375 11.25V18.75C3.4375 24.5125 5.4875 26.5625 11.25 26.5625H18.75C24.5125 26.5625 26.5625 24.5125 26.5625 18.75V11.25C26.5625 5.4875 24.5125 3.4375 18.75 3.4375H11.25Z" fill="currentColor"/> </svg>`;
  }

}

declare global {
  interface Window { TraderverseStockCompanyStats?: typeof TraderverseStockCompanyStats; }
}

if (typeof window !== 'undefined') {
  window.TraderverseStockCompanyStats = TraderverseStockCompanyStats;
}
